/**
 * PrintCoverPage — delat försättsblad för WorkshopN och SwotN utskrift.
 * Renderas som första print-page, döljer sig på skärm via no-print (eller
 * visas inbäddat i förhandsgranskning om showOnScreen=true).
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const accent = '#E05500';

const TYPE_CONFIG = {
  workshop: {
    label:   'WORKSHOP',
    color:   accent,
    bgColor: '#FFF5F0',
    borderColor: '#FFD5B8',
  },
  swot: {
    label:   'SWOT-ANALYS',
    color:   '#42A5F5',
    bgColor: '#F0F7FF',
    borderColor: '#90CAF9',
  },
};

/**
 * Props:
 *   type          'workshop' | 'swot'
 *   title         string
 *   subtitle      string | null   — frågeställning / underrubrik
 *   date          Date | null
 *   facilitator   string | null
 *   groups        [{id, name, color}] | null
 *   questions     string[]  | null  — workshop-frågor (visas om ≤8)
 *   companyLogo   url | null
 *   imageUrl      url | null       — omslagsbild om finns
 *   isA4          bool
 */
export default function PrintCoverPage({
  type, title, subtitle, date, facilitator,
  groups, questions, companyLogo, imageUrl, isA4,
}) {
  const tc = TYPE_CONFIG[type] || TYPE_CONFIG.workshop;

  const formattedDate = date
    ? date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const showQuestions = questions?.length > 0 && questions.length <= 8;

  return (
    <Box
      className="print-page"
      sx={{
        width: '100%',
        maxWidth: isA4 ? 750 : 1060,
        mx: 'auto',
        mb: 4,
        bgcolor: '#fff',
        color: '#000',
        display: 'flex',
        flexDirection: 'column',
        minHeight: isA4 ? 440 : 620,
        border: `1px solid ${tc.borderColor}`,
        borderRadius: 2,
        overflow: 'hidden',
        '@media print': {
          height: isA4 ? '194mm' : '281mm',
          overflow: 'hidden',
          maxWidth: '100%',
          mb: 0,
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
        },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: isA4 ? 6 : 9, bgcolor: tc.color, flexShrink: 0 }} />

      {/* Main content area — vertically centered */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: isA4 ? 5 : 7,
        pt: isA4 ? 2 : 3,
        pb: isA4 ? 1 : 1.5,
        gap: isA4 ? 1.5 : 2.5,
        textAlign: 'center',
      }}>

        {/* Company logo */}
        {companyLogo && (
          <Box
            component="img"
            src={companyLogo}
            alt=""
            sx={{
              maxHeight: isA4 ? 52 : 76,
              maxWidth: isA4 ? 220 : 320,
              objectFit: 'contain',
            }}
          />
        )}

        {/* Type badge */}
        <Box sx={{
          px: isA4 ? 1.75 : 2.5,
          py: 0.5,
          bgcolor: tc.bgColor,
          border: `1.5px solid ${tc.color}66`,
          borderRadius: 100,
        }}>
          <Typography
            fontSize={isA4 ? '0.6rem' : '0.76rem'}
            fontWeight={800}
            color={tc.color}
            letterSpacing={2.5}
          >
            {tc.label}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          fontSize={isA4 ? '1.55rem' : '2.1rem'}
          fontWeight={800}
          color="#111"
          lineHeight={1.2}
          sx={{ maxWidth: '88%' }}
        >
          {title}
        </Typography>

        {/* Subtitle / question */}
        {subtitle && (
          <Typography
            fontSize={isA4 ? '0.76rem' : '0.96rem'}
            color="#555"
            fontStyle="italic"
            lineHeight={1.45}
            sx={{ maxWidth: '75%' }}
          >
            "{subtitle}"
          </Typography>
        )}

        {/* Session image */}
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{
              maxHeight: isA4 ? 64 : 96,
              maxWidth: isA4 ? 320 : 480,
              objectFit: 'cover',
              borderRadius: 1.5,
              border: '1px solid #eee',
            }}
          />
        )}

      </Box>

      {/* Bottom metadata strip */}
      <Box sx={{
        borderTop: `1px solid ${tc.borderColor}55`,
        px: isA4 ? 4 : 6,
        py: isA4 ? 1.25 : 2,
        display: 'flex',
        gap: isA4 ? 3 : 5,
        alignItems: 'flex-start',
        flexShrink: 0,
        flexWrap: 'wrap',
        bgcolor: tc.bgColor + '55',
      }}>

        {/* Datum */}
        {formattedDate && (
          <Box>
            <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} color="#aaa" letterSpacing={1} mb={0.3} textTransform="uppercase">
              Datum
            </Typography>
            <Typography fontSize={isA4 ? '0.68rem' : '0.84rem'} fontWeight={600} color="#333">
              {formattedDate}
            </Typography>
          </Box>
        )}

        {/* Facilitator */}
        {facilitator && (
          <Box>
            <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} color="#aaa" letterSpacing={1} mb={0.3} textTransform="uppercase">
              Facilitator
            </Typography>
            <Typography fontSize={isA4 ? '0.68rem' : '0.84rem'} fontWeight={600} color="#333">
              {facilitator}
            </Typography>
          </Box>
        )}

        {/* Grupper */}
        {groups?.length > 0 && (
          <Box>
            <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} color="#aaa" letterSpacing={1} mb={0.5} textTransform="uppercase">
              Grupper
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {groups.map((g, i) => (
                <Box
                  key={g.id || g.name || i}
                  sx={{
                    px: isA4 ? 0.9 : 1.2,
                    py: 0.2,
                    borderRadius: 100,
                    bgcolor: (g.color || accent) + '18',
                    border: `1.5px solid ${g.color || accent}`,
                  }}
                >
                  <Typography fontSize={isA4 ? '0.58rem' : '0.72rem'} fontWeight={700} color={g.color || accent}>
                    {g.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Workshop-frågor */}
        {showQuestions && (
          <Box sx={{ flex: 1, minWidth: isA4 ? 140 : 200 }}>
            <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} color="#aaa" letterSpacing={1} mb={0.5} textTransform="uppercase">
              Frågor ({questions.length})
            </Typography>
            {questions.map((q, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.2 }}>
                <Typography fontSize={isA4 ? '0.55rem' : '0.68rem'} color={tc.color} fontWeight={700} flexShrink={0}>
                  {i + 1}.
                </Typography>
                <Typography fontSize={isA4 ? '0.55rem' : '0.68rem'} color="#444" lineHeight={1.35}>
                  {q}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Antal frågor om för många att lista */}
        {!showQuestions && questions?.length > 0 && (
          <Box>
            <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} color="#aaa" letterSpacing={1} mb={0.3} textTransform="uppercase">
              Frågor
            </Typography>
            <Typography fontSize={isA4 ? '0.68rem' : '0.84rem'} fontWeight={600} color="#333">
              {questions.length} st
            </Typography>
          </Box>
        )}

      </Box>

      {/* Bottom thin accent line */}
      <Box sx={{ height: isA4 ? 3 : 4, bgcolor: tc.color + '55', flexShrink: 0 }} />
    </Box>
  );
}
